import { useEffect, useState } from 'react';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import {
  getOnboardingAnalytics,
  type OnboardingAnalyticsResponse,
  type RecentSession,
} from '../api/onboarding-analytics';
import { formatRelativeTime } from '../utils/formatters';

interface StatsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

function StatsCard({ title, value, subtitle, icon, color }: StatsCardProps) {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorStyles[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </Card>
  );
}

function StatusBadge({ status }: { status: RecentSession['status'] }) {
  const styles = {
    completed: 'bg-green-100 text-green-700',
    in_progress: 'bg-blue-100 text-blue-700',
    abandoned: 'bg-red-100 text-red-700',
  };

  const labels = {
    completed: 'Completed',
    in_progress: 'In Progress',
    abandoned: 'Abandoned',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function SourceBadge({ source }: { source: string | null }) {
  const sourceKey = source || 'unknown';
  const styles: Record<string, string> = {
    checkout: 'bg-purple-100 text-purple-700',
    magic_link: 'bg-blue-100 text-blue-700',
    admin: 'bg-gray-100 text-gray-700',
    unknown: 'bg-gray-100 text-gray-500',
  };

  const labels: Record<string, string> = {
    checkout: 'Checkout',
    magic_link: 'Magic Link',
    admin: 'Admin',
    unknown: 'Unknown',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[sourceKey] || styles.unknown}`}>
      {labels[sourceKey] || sourceKey}
    </span>
  );
}

export function OnboardingAnalyticsPage() {
  const [data, setData] = useState<OnboardingAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await getOnboardingAnalytics();
        setData(result);
      } catch (err) {
        setError('Failed to load onboarding analytics');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <PageContainer title="Onboarding Analytics">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer title="Onboarding Analytics">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          {error || 'Failed to load data'}
        </div>
      </PageContainer>
    );
  }

  const { stats, funnel, by_source, recent_sessions, avg_completion_time_hours } = data;

  return (
    <PageContainer
      title="Onboarding Analytics"
      subtitle="Track onboarding completion rates and funnel performance"
    >
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Total Sessions"
          value={stats.total}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          color="blue"
        />
        <StatsCard
          title="Completion Rate"
          value={`${stats.completion_rate}%`}
          subtitle={`${stats.completed} completed`}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="green"
        />
        <StatsCard
          title="In Progress"
          value={stats.in_progress}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="yellow"
        />
        <StatsCard
          title="Avg. Completion Time"
          value={avg_completion_time_hours ? `${avg_completion_time_hours}h` : 'N/A'}
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
          color="purple"
        />
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Step Funnel */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step Funnel</h2>
          <div className="space-y-3">
            {funnel.map((step) => (
              <div key={step.step}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">
                    {step.step}. {step.step_name}
                  </span>
                  <span className="text-gray-500">
                    {step.count} ({step.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ width: `${step.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Source Breakdown */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sessions by Source</h2>
          {by_source.length > 0 ? (
            <div className="space-y-4">
              {by_source.map((source) => (
                <div key={source.source} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        source.source === 'checkout'
                          ? 'bg-purple-500'
                          : source.source === 'magic_link'
                          ? 'bg-blue-500'
                          : source.source === 'admin'
                          ? 'bg-gray-500'
                          : 'bg-gray-300'
                      }`}
                    />
                    <span className="text-gray-700 capitalize">
                      {source.source === 'magic_link' ? 'Magic Link' : source.source}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium text-gray-900">{source.count}</span>
                    <span className="text-gray-500 text-sm ml-2">({source.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No data yet</p>
          )}
        </Card>
      </div>

      {/* Recent Sessions Table */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Sessions</h2>
        {recent_sessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Step
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recent_sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-medium text-gray-900">
                        {session.business_name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {session.customer_email}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                        {session.current_step}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge status={session.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <SourceBadge source={session.source} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(session.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No onboarding sessions yet</p>
        )}
      </Card>
    </PageContainer>
  );
}
